import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Download, Plus, ChevronRight, ChevronLeft, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const ClassificationList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate(); 
    
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [skip, setSkip] = useState(0);
    const limit = 20;
    const { canCreate } = usePermissions('classifications');
    const { fontSize } = useFontSize();

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const fetchClassifications = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/classifications?skip=${skip}&limit=${limit}&search=${searchTerm}`);
            setList(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Fetch Error");
        } finally {
            setLoading(false);
        }
    }, [skip, searchTerm]);

    useEffect(() => {
        const timer = setTimeout(() => fetchClassifications(), 500);
        return () => clearTimeout(timer);
    }, [fetchClassifications]);

    const handleRowClick = (id) => {
        navigate(`/classifications/${id}`);
    };

    return (
        <div className={`p-4 bg-surface-app min-h-screen ${isRTL ? 'font-cairo' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }}>
            <div className="max-w-7xl mx-auto bg-surface-card border shadow-sm rounded-sm overflow-hidden">
                {/* Toolbar */}
                <div className={`p-2 border-b flex justify-between items-center bg-surface-panel ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex gap-4 items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <span className="text-xs font-bold text-ink-secondary uppercase">{t('menu.classifications')}</span>
                        <div className="relative">
                            <input
                                className={`border p-1 text-[0.9em] outline-none w-64 ${isRTL ? 'pr-7 text-right' : 'pl-7 text-left'}`}
                                placeholder={t('domains.find_placeholder')}
                                value={searchTerm}
                                onChange={(e) => {setSearchTerm(e.target.value); setSkip(0);}}
                            />
                            <Search className={`absolute top-1.5 text-ink-secondary ${isRTL ? 'right-2' : 'left-2'}`} size={13} />
                        </div>
                        {loading && <RefreshCw size={14} className="animate-spin text-primary" />}
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button className="p-1 hover:bg-surface-panel rounded"><Download size={14}/></button>
                        {canCreate && (
                        <button className="bg-primary text-white px-3 py-1 text-[11px] font-bold flex items-center gap-1 hover:bg-primary-hover transition-all"
                                onClick={() => handleRowClick('new')}>
                            <Plus size={12}/> {t('sidebar.new')} {t('apps.CLASSIFICATION')}
                        </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-[0.9em] border-collapse">
                        <thead className="bg-surface-panel text-ink-secondary font-bold uppercase border-b">
                            <tr className={`${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                                <th className="p-3 border-r">{t('apps.CLASSIFICATION')}</th>
                                <th className="p-3 border-r">{t('organizations.description')}</th>
                                <th className="p-3 border-r">{t('classifications.parent')}</th>
                                <th className="p-3">{t('classifications.order')}</th>
                            </tr>
                        </thead>
                        <tbody className={loading ? 'opacity-50' : ''}>
                            {list?.map((item, idx) => (
                                <tr key={idx} className={`border-b hover:bg-primary-soft cursor-pointer group ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                                    onClick={() => handleRowClick(item.classstructureid)}>
                                    <td className="p-3 font-bold text-primary underline group-hover:text-primary-hover">{item.classificationid}</td>
                                    <td className="p-3 text-ink">{item.description}</td>
                                    <td className="p-3 text-ink-secondary font-medium">{item.parent || '---'}</td>
                                    <td className="p-3 text-ink-secondary">{item.order || '0'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Controls */}
                <div className={`p-3 bg-surface-panel border-t flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="text-ink-secondary font-bold">
                        {t('dashboard.showing')} {skip + 1} - {Math.min(skip + limit, total)} {t('dashboard.of')} {total}
                    </div>
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <button
                            disabled={skip === 0}
                            onClick={() => setSkip(prev => Math.max(0, prev - limit))}
                            className="p-1.5 bg-surface-card border rounded shadow-sm disabled:opacity-30 hover:bg-surface-panel"
                        >
                            {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                        </button>
                        <button
                            disabled={skip + limit >= total}
                            onClick={() => setSkip(prev => prev + limit)}
                            className="p-1.5 bg-surface-card border rounded shadow-sm disabled:opacity-30 hover:bg-surface-panel"
                        >
                            {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClassificationList;